import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import {
  ButtonPrimary,
  createBasicColorSet,
  TextLink,
} from '@spotify-internal/encore-web';
import MarketContext from '../../../configuration/context/market';
import { /* isArabicMarket, */ purify } from '../../utils/helpers';
// import DirectionContext from '../../../configuration/context/direction';
import DeviceContext from '../../../configuration/context/device';
import * as url from 'url';

export type ButtonPropTypes = React.ComponentProps<typeof ButtonPrimary> & {
  children?: string;
  href?: string;
  label?: string;
  referralSource?: string;
  insertMarket?: boolean;
  backgroundColor?: string;
  image?: { src: string; alt?: string };
  $borderValues?: {
    color: string;
    width: number;
    gradient?: {
      from: string;
      to: string;
    };
  };
  isPreloader?: boolean;
  dataEventCategory?: string;
  dataEventAction?: string;
  dataEventLabel?: string;

  // deprecated
  color?: string;
  border?: string;
};

const appendReferralParamToButton = (
  link: string,
  referralSource: string,
): string => {
  if (!referralSource) {
    return link;
  }
  return link.includes('?')
    ? `${link}&ref=${referralSource}`
    : `${link}?ref=${referralSource}`;
};

// figures out if it's a checkout link and how to append country if so
const appendCountryParamToButton = (link: string, country: string): string => {
  const urlObject = url.parse(link, true);
  if (!country || !urlObject?.pathname?.includes('/purchase/')) {
    return link;
  }

  return url.format({
    ...urlObject,
    query: {
      ...urlObject.query,
      country,
    },
    search: null,
  });
};

const prependLinkWithMarket = (link: string, market: string | null): string => {
  if (!market) {
    return link;
  }

  let output = link;

  if (output[0] === '/' && output[1] !== '/') {
    const parsedOutput: string[] = output.split('/');
    if (parsedOutput[1] !== market) {
      output = `/${market}${link}`;
    }
  }

  return output;
};

const ImageButton = styled.img`
  margin: 0 auto;
`;

export const Button = styled(ButtonComponent)`
  text-decoration: none !important;
  justify-content: center;
  text-transform: capitalize;

  ${({ border = '' }) => border && `border: ${border};`}
  ${({ $borderValues }) =>
    $borderValues?.width &&
    `
      border-width: ${$borderValues.width}px;
      border-style: solid;
    `}
  
  ${({ $borderValues }) => {
    if ($borderValues?.gradient) return 'border-color: transparent;';
    if ($borderValues?.color) return `border-color: ${$borderValues?.color};`;
    return '';
  }};

${({ $borderValues }) =>
  $borderValues?.gradient &&
  `
      --gradient-position: left;
      
      border-style: solid;
      background: linear-gradient(var(--background-base), var(--background-base)) padding-box,
                  linear-gradient(to right, ${$borderValues?.gradient.from}, ${$borderValues?.gradient.to}, ${$borderValues?.gradient.from}) border-box;
      background-size: auto, 200%;
      background-position: center, top var(--gradient-position);

      @media (prefers-reduced-motion: no-preference) {
        transition: background-position .6s;
      }
      &:hover {
        --gradient-position: right;
      }
      
      `}}}
`;

function ButtonComponent(props: ButtonPropTypes) {
  const {
    /*
      `backgroundColor` is used in the outer wrapping styled component, and
      we don't want to pass this down to ButtonPrimary with `rest`, so we
      destruct it out of props but never use it here.
    */
    backgroundColor = '#000',
    color,
    isPreloader = false,
    children,
    referralSource,
    image,
    href,
    label,
    insertMarket = true,
    dataEventAction,
    dataEventCategory,
    dataEventLabel,
    ...rest
  } = props;
  const [hasHovered, setHasHovered] = useState(false);
  const market = useContext(MarketContext);
  const device = useContext(DeviceContext);

  const modifiedHref =
    href && market.country
      ? appendCountryParamToButton(href, market.country.toUpperCase())
      : href;

  const configuredHref =
    insertMarket && modifiedHref
      ? prependLinkWithMarket(
          referralSource
            ? appendReferralParamToButton(modifiedHref, referralSource)
            : modifiedHref,
          market?.market ?? null,
        )
      : modifiedHref;

  const shouldRender = !(device.platform === 'ios' && !href?.startsWith('#'));

  const handleCtaHover = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isPreloader && !hasHovered) {
      const hintNode = document.createElement('link');
      hintNode.rel = 'prerender';
      hintNode.as = 'document';
      hintNode.dataset.referralId = 'button';
      hintNode.dataset.referralType = 'primary-cta';
      hintNode.href = e.currentTarget.href;
      document.head.appendChild(hintNode);
      setHasHovered(true);
    }
  };

  return shouldRender ? (
    <div>
      {!image && (
        // issue seems similar to typescript issue seen in BasePage.tsx with masthead incompatibility issue.
        // @ts-ignore
        <ButtonPrimary
          UNSAFE_colorSet={createBasicColorSet(backgroundColor, color)}
          aria-label={label}
          data-event-category={dataEventCategory}
          data-event-action={dataEventAction}
          data-event-label={dataEventLabel}
          href={configuredHref}
          onMouseEnter={handleCtaHover}
          tabIndex={0}
          {...rest}
        >
          {children && (
            <span dangerouslySetInnerHTML={{ __html: purify(children) }} />
          )}
        </ButtonPrimary>
      )}
      {image && (
        <TextLink
          aria-label={label}
          standalone
          href={configuredHref}
          onMouseEnter={handleCtaHover}
        >
          <ImageButton src={image.src} alt={label ? '' : image.alt ?? ''} />
        </TextLink>
      )}
    </div>
  ) : (
    <></>
  );
}

export default Button;
